import React from "react";
import { Link } from "react-router-dom";
import { PublicShell } from "@/pages/Syllabus";

const UPDATED = "October 2026";

const POLICIES = [
  {
    id: "status",
    title: "Accreditation status",
    body: [
      "Scholar Campus is not currently accredited by a regional, national or state-recognized accrediting agency. We will update this page if and when that status changes.",
      "Families and schools should confirm with a receiving school, district or college how Scholar Campus coursework and records will be recognized.",
    ],
  },
  {
    id: "grading",
    title: "Grading policy",
    body: [
      "Each lesson ends with a 5-question mastery quiz. A lesson is mastered at 60% or higher. Quizzes may be retaken, and the best score is kept.",
      "A course grade is the average of the best quiz scores across all of its lessons, recorded once every lesson is complete: A 90-100, B 80-89, C 70-79, D 60-69, F below 60.",
      "GPA is unweighted on a 4.0 scale (A 4, B 3, C 2, D 1, F 0) and weighted by credit. High school credits and college semester hours are reported as separate GPAs.",
      "Exam-prep courses (AP, SAT/ACT, GED, STAAR and professional certification prep) are non-credit. They are not College Board-authorized AP courses, so no AP grade weighting is applied.",
    ],
  },
  {
    id: "credit",
    title: "Credit policy",
    body: [
      "Credit values are set by the school administrator for each course and shown on its syllabus. By default, core high school courses carry 0.5 credit, core college courses carry 3 semester hours, and Pre-K-8 courses are graded without credit.",
      "Credit is earned when every lesson in a course is complete with a passing course grade (D or higher).",
      "Outside, transfer and dual-credit coursework can be recorded by a parent or administrator. It appears on the transcript labeled as transfer credit with its source.",
    ],
  },
  {
    id: "attendance",
    title: "Attendance & instructional time",
    body: [
      "Attendance is recorded automatically. Time counts only while a student is actively working (lesson, quiz or AI tutor) with the page open and in use. Idle time is not counted.",
      "A day is recorded as present after 15 minutes of active learning. Daily minutes, school-year instructional hours and a downloadable attendance log are available to the student, their parent and their teachers.",
      "Home school families remain responsible for meeting their state's attendance and instruction requirements. In Texas, home schools must provide a bona fide curriculum in reading, spelling, grammar, math and good citizenship.",
    ],
  },
  {
    id: "teachers",
    title: "Teacher qualifications",
    body: [
      "Teachers can upload their certifications and credentials (for example a Texas Standard Certificate). A Scholar Campus administrator reviews each document and marks it verified or rejected.",
      "Teacher credential files are private. Only the teacher, their school account and administrators can view them.",
    ],
  },
  {
    id: "integrity",
    title: "Academic integrity & AI use",
    body: [
      "Students must do their own work on quizzes. Sage, the AI tutor, is designed to guide students step by step rather than hand out answers, and it adjusts to the student's grade level.",
      "AI-written lessons are labeled. Starter-library lessons cite the standards they align to, and teachers can review or edit any lesson they assign.",
    ],
  },
  {
    id: "privacy",
    title: "Student privacy (FERPA & COPPA)",
    body: [
      "Student records (grades, attendance, transcripts) are shared only with the student, their linked parent or guardian, their teachers and school, and administrators.",
      "Children under 13 are added by a parent, teacher or school. Parents may review their child's records at any time and request corrections or deletion by contacting the school.",
      "We do not sell student data or use it for advertising.",
    ],
  },
  {
    id: "records",
    title: "Records retention",
    body: [
      "Transcripts, grades and attendance logs are kept for the life of the student's account and can be printed or saved as PDF at any time from the Records page.",
    ],
  },
  {
    id: "access",
    title: "Accessibility & non-discrimination",
    body: [
      "Scholar Campus is built for keyboard navigation and screen readers and offers voice input and read-aloud with the AI tutor.",
      "Scholar Campus welcomes students of every race, color, national origin, sex, disability, religion and age, and does not discriminate in admission, access or treatment.",
    ],
  },
];

export default function Policies() {
  return (
    <PublicShell>
      <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground font-display">Scholar Campus · Last updated {UPDATED}</div>
      <h1 className="text-3xl sm:text-4xl font-display font-semibold tracking-tight" data-testid="policies-title">School policies</h1>
      <p className="mt-2 text-muted-foreground max-w-2xl">How we grade, award credit, take attendance, qualify teachers and protect student records. Every course also has a public <Link to="/syllabi" className="text-[#0B6B6B] underline">syllabus</Link>.</p>
      <div className="mt-8 grid lg:grid-cols-[220px_1fr] gap-8">
        <nav className="hidden lg:block sticky top-6 self-start text-sm space-y-1" data-testid="policies-toc">
          {POLICIES.map((p) => <a key={p.id} href={`#${p.id}`} className="block py-1 text-muted-foreground hover:text-[#0B6B6B]">{p.title}</a>)}
        </nav>
        <div className="space-y-6">
          {POLICIES.map((p) => (
            <section key={p.id} id={p.id} className="bg-white border border-border rounded-[14px] p-6 scroll-mt-6" data-testid={`policy-${p.id}`}>
              <h2 className="text-xl font-display font-semibold">{p.title}</h2>
              <div className="mt-3 space-y-2 text-[#123645] leading-relaxed">{p.body.map((b) => <p key={b}>{b}</p>)}</div>
            </section>
          ))}
        </div>
      </div>
    </PublicShell>
  );
}
